import React from 'react'
import IntegrationCard from '@/features/integrations/components/IntegrationCard'
import OutlookWorkspaceConnectModal from './OutlookWorkspaceConnectModal'
import { useOutlookWorkspaceIntegration } from '../hooks/useOutlookWorkspaceIntegration'
import { combineOutlookWorkspaceStatus } from '../lib/status'
import type { IntegrationSummary } from '@/features/integrations/types'

const OutlookIcon: React.FC = () => (
  <svg width="20" height="20" viewBox="0 0 48 48" aria-hidden="true">
    <path fill="#0A2767" d="M44 18.5v16.9a2.1 2.1 0 0 1-2.1 2.1H28V22.1L30 20l12.6-6.2A1.9 1.9 0 0 1 44 15.4v3.1Z" />
    <path fill="#0364B8" d="M28 6H9.1A2.1 2.1 0 0 0 7 8.1v8.3l10.5 6.2L28 28V6Z" />
    <path fill="#28A8EA" d="M28 6v22l-10.5-6.2L7 16.4" opacity=".6" />
    <path fill="#0078D4" d="M7 16.4v15.2A2.1 2.1 0 0 0 9.1 33.7H28V22.1L7 16.4Z" />
    <path fill="#50D9FF" d="M28 22.1v16H9.1A2.1 2.1 0 0 1 7 36v-3.9L28 22.1Z" opacity=".3" />
    <circle cx="19" cy="24" r="8" fill="#0A2767" opacity=".1" />
    <path fill="#fff" d="M19 18.5c-3.3 0-6 2.5-6 5.9s2.7 5.9 6 5.9 6-2.5 6-5.9-2.7-5.9-6-5.9Zm0 9.6c-1.9 0-3.4-1.6-3.4-3.7s1.5-3.7 3.4-3.7 3.4 1.6 3.4 3.7-1.5 3.7-3.4 3.7Z" />
  </svg>
)

type Props = {
  workspaceId: string
  outlookSummary: IntegrationSummary | undefined
  outlookCalendarSummary: IntegrationSummary | undefined
  onRefresh: () => void
}

const OutlookWorkspaceIntegrationCard: React.FC<Props> = ({ workspaceId, outlookSummary, outlookCalendarSummary, onRefresh }) => {
  const {
    connecting,
    disconnecting,
    showExplainer,
    openConnectModal,
    closeConnectModal,
    confirmConnect,
    disconnect,
  } = useOutlookWorkspaceIntegration(workspaceId, onRefresh)

  const status = combineOutlookWorkspaceStatus(outlookSummary, outlookCalendarSummary)
  const identifier = outlookSummary?.identifier ?? outlookCalendarSummary?.identifier
  // Mirrors GoogleWorkspaceIntegrationCard: when active, append a "Main" tag
  // to the detail line so Settings reads as the source of the main mailbox.
  const connectedDetail = identifier && status === 'active' ? `${identifier}  ·  Main` : identifier

  const handleDisconnect = async () => {
    await disconnect()
    onRefresh()
  }

  return (
    <>
      <IntegrationCard
        icon={<OutlookIcon />}
        name="Outlook Workspace"
        description="Outlook Mail for email and Outlook Calendar for scheduling — one connection, one consent."
        status={status}
        connectedDetail={connectedDetail}
        connecting={connecting}
        disconnecting={disconnecting}
        onConnect={openConnectModal}
        onDisconnect={handleDisconnect}
        connectLabel="Connect Workspace"
        disconnectConfirmTitle="Disconnect Outlook Workspace"
        disconnectConfirmMessage={`Remove the Outlook Workspace integration for ${identifier ?? 'this account'}? Zeph will stop reading/sending email and reading your calendar immediately.`}
      />

      {showExplainer && (
        <OutlookWorkspaceConnectModal
          connecting={connecting}
          onConfirm={confirmConnect}
          onCancel={closeConnectModal}
        />
      )}
    </>
  )
}

export default React.memo(OutlookWorkspaceIntegrationCard)
